import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { parseSchoolLetter } from '@kraftwerk/shared';
import type { LetterReading, LetterSuggestion } from '@kraftwerk/shared';
import type { TFunction } from 'i18next';
import { db } from '../../db/local.js';
import type { LocalHouseholdMember, LocalTaskList } from '../../db/local.js';
import { currentHouseholdId, listMembers } from '../../db/shopping.js';
import { listTaskLists } from '../../db/tasks.js';
import { formatDay } from '../../format.js';
import { useTimeZone } from '../../hooks/useToday.js';
import { useSwipeBack } from '../../gestures/SwipeProvider.js';
import { applyLetter, defaultListId, NEW_LIST, rowIsComplete } from '../../letter/applyLetter.js';
import type { LetterRow } from '../../letter/applyLetter.js';
import { OcrAbortedError, recognizeOnDevice } from '../../letter/ocr.js';
import type { OcrProgress, Recognize } from '../../letter/ocr.js';
import { formatMoney } from '../shopping/Costs.js';

type Phase =
  | { name: 'start'; typing: boolean }
  | { name: 'reading'; progress: OcrProgress }
  | { name: 'review' }
  | { name: 'saved'; events: number; tasks: number };

type Problem = 'ocr' | 'offline' | 'save' | null;

/** Titel in der Sprache der App — Wörter aus dem Brief bleiben, wie sie dort stehen. */
export function suggestionTitle(
  suggestion: LetterSuggestion,
  t: TFunction,
  language: string,
): string {
  const { title } = suggestion;
  const text = (() => {
    switch (title.type) {
      case 'name':
      case 'sentence':
        return title.text || t('letter.titles.untitled');
      case 'bring':
        return t('letter.titles.bring', { items: suggestion.items.join(', ') });
      case 'deadline':
        return title.subject
          ? t('letter.titles.deadline', { subject: title.subject })
          : t('letter.titles.deadlineGeneric');
      case 'money': {
        if (suggestion.amountCents === null) return t('letter.titles.moneyUnclear');
        const amount = formatMoney(suggestion.amountCents, language);
        if (title.transfer)
          return title.subject
            ? t('letter.titles.transferFor', { amount, subject: title.subject })
            : t('letter.titles.transfer', { amount });
        return title.subject
          ? t('letter.titles.moneyFor', { amount, subject: title.subject })
          : t('letter.titles.money', { amount });
      }
    }
  })();
  return text.slice(0, 200);
}

/**
 * Eine Zeile, wie sie im Bildschirm steht. `null` bei „für wen“ und Liste heißt
 * „wie die Vorgabe“ — die kommt aus Live-Abfragen und kann nach dem Lesen noch
 * nachziehen (Mitglieder, Listen).
 */
type DraftRow = Omit<LetterRow, 'forUserId' | 'listId'> & {
  forUserId: string | null;
  listId: string | null;
};

function rowsOf(reading: LetterReading, t: TFunction, language: string): DraftRow[] {
  return reading.suggestions.map((suggestion, index) => ({
    key: `${index}-${suggestion.sourceLine.slice(0, 20)}`,
    selected: suggestion.confidence !== 'low',
    kind: suggestion.kind,
    title: suggestionTitle(suggestion, t, language),
    date: suggestion.date ?? '',
    time: suggestion.time ?? '',
    durationMin: suggestion.durationMin,
    endDate: suggestion.endDate,
    forUserId: null,
    listId: null,
  }));
}

/**
 * Elternbrief-Leser (27.10.2026): Foto eines Zettels aus Schule oder Kita →
 * Texterkennung **auf dem Gerät** → Vorschläge für Termine und Aufgaben, jeder
 * einzeln prüf- und änderbar → „Übernehmen“ schreibt über die vorhandenen
 * Schreibfunktionen. Das Foto wird nie gespeichert und nie gesendet; der
 * erkannte Text lebt nur in diesem Bildschirm.
 *
 * `recognize` ist austauschbar, damit Tests ohne WebAssembly laufen.
 */
export function LetterReader({
  onClose,
  recognize = recognizeOnDevice,
}: {
  onClose: () => void;
  recognize?: Recognize;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const ids = { camera: useId(), pick: useId(), text: useId(), forWhom: useId() };
  const camera = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);
  const [phase, setPhase] = useState<Phase>({ name: 'start', typing: false });
  const [problem, setProblem] = useState<Problem>(null);
  const [text, setText] = useState('');
  const [reading, setReading] = useState<LetterReading | null>(null);
  const [rows, setRows] = useState<DraftRow[]>([]);
  /** `null`: nicht gewählt — dann das einzige Kind, sonst „für alle“. */
  const [forWhom, setForWhom] = useState<string | null>(null);
  const [textOpen, setTextOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const householdId = useLiveQuery(() => currentHouseholdId(db), [], null);
  const members = useLiveQuery(
    () => listMembers(householdId ?? null, db),
    [householdId],
    [] as LocalHouseholdMember[],
  );
  const lists = useLiveQuery(() => listTaskLists(db), [], [] as LocalTaskList[]);
  const children = members.filter((member) => member.role === 'child');
  // Kinder zuerst — um sie geht es in einem Elternbrief.
  const people = [...children, ...members.filter((member) => member.role !== 'child')];
  const usableLists = lists.filter(
    (list) => list.kind === 'tasks' && (householdId ? list.householdId === householdId : true),
  );
  const letterFor = forWhom ?? (children.length === 1 ? children[0]!.userId : '');
  const resolve = (row: DraftRow): LetterRow => ({
    ...row,
    forUserId: row.forUserId ?? letterFor,
    listId: row.listId ?? defaultListId(lists, householdId ?? null),
  });

  // Laufende Erkennung beim Verlassen beenden.
  useEffect(() => () => abort.current?.abort(), []);

  const back = () => {
    abort.current?.abort();
    onClose();
  };
  useSwipeBack(phase.name === 'reading' || saving ? null : back);

  const analyze = (source: string) => {
    const result = parseSchoolLetter(
      source,
      new Date(),
      timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    setReading(result);
    setRows(rowsOf(result, t, i18n.language));
    setTextOpen(result.suggestions.length === 0);
    setProblem(null);
    setPhase({ name: 'review' });
  };

  const read = (file: File | undefined) => {
    if (!file) return;
    const controller = new AbortController();
    abort.current = controller;
    setProblem(null);
    setPhase({ name: 'reading', progress: { stage: 'loading', progress: 0 } });
    recognize(
      file,
      (progress) => {
        if (!controller.signal.aborted) setPhase({ name: 'reading', progress });
      },
      controller.signal,
    )
      .then((recognized) => {
        if (controller.signal.aborted) return;
        setText(recognized.trim());
        analyze(recognized);
      })
      .catch((error: unknown) => {
        if (error instanceof OcrAbortedError || controller.signal.aborted) return;
        // Ohne Netz kommt die Texterkennung beim ersten Mal nicht — das ist ein
        // anderer Weg zum Ziel als ein unscharfes Foto.
        setProblem(navigator.onLine === false ? 'offline' : 'ocr');
        setPhase({ name: 'start', typing: false });
      });
  };

  const update = (key: string, change: Partial<DraftRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...change } : row)));

  const chosen = rows.filter((row) => row.selected).map(resolve);
  const incomplete = chosen.some((row) => !rowIsComplete(row));

  const save = () => {
    setSaving(true);
    setProblem(null);
    applyLetter(
      chosen,
      { householdId: householdId ?? null, newListName: t('letter.newListName') },
      db,
    )
      .then((result) => setPhase({ name: 'saved', ...result }))
      .catch(() => setProblem('save'))
      .finally(() => setSaving(false));
  };

  const restart = () => {
    setText('');
    setReading(null);
    setRows([]);
    setProblem(null);
    setPhase({ name: 'start', typing: false });
  };

  return (
    <section aria-labelledby="letter-heading" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 id="letter-heading" className="font-display text-xl font-bold">
          {t('letter.heading')}
        </h2>
        <button
          type="button"
          onClick={back}
          disabled={saving}
          className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
        >
          {t('letter.back')}
        </button>
      </div>

      {phase.name === 'start' ? (
        <div className="kw-tile flex flex-col gap-3">
          <p className="text-sm">{t('letter.privacy')}</p>
          <p className="text-xs text-ink-muted">{t('letter.firstLoad')}</p>
          <label htmlFor={ids.camera} className="sr-only">
            {t('letter.camera')}
          </label>
          <input
            ref={camera}
            id={ids.camera}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            tabIndex={-1}
            onChange={(event) => {
              read(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <label htmlFor={ids.pick} className="sr-only">
            {t('letter.pick')}
          </label>
          <input
            ref={picker}
            id={ids.pick}
            type="file"
            accept="image/*"
            className="sr-only"
            tabIndex={-1}
            onChange={(event) => {
              read(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={() => camera.current?.click()} className="kw-btn flex-1">
              {t('letter.camera')}
            </button>
            <button
              type="button"
              onClick={() => picker.current?.click()}
              className="kw-btn kw-btn-quiet flex-1"
            >
              {t('letter.pick')}
            </button>
          </div>
          {problem === 'ocr' || problem === 'offline' ? (
            <p role="alert" className="text-sm text-warn">
              {t(`letter.error.${problem}`)}
            </p>
          ) : null}
          {phase.typing ? (
            <TextInput
              id={ids.text}
              value={text}
              onChange={setText}
              onAnalyze={() => analyze(text)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setPhase({ name: 'start', typing: true })}
              className="kw-btn kw-btn-quiet kw-btn-small"
            >
              {t('letter.typeIt')}
            </button>
          )}
        </div>
      ) : null}

      {phase.name === 'reading' ? (
        <div className="kw-tile flex flex-col gap-3" aria-live="polite">
          <p className="text-sm">{t('letter.privacy')}</p>
          <p className="font-semibold">
            {phase.progress.stage === 'reading' ? t('letter.reading') : t('letter.loading')}
          </p>
          <span
            className="kw-bar"
            role="progressbar"
            aria-label={
              phase.progress.stage === 'reading' ? t('letter.reading') : t('letter.loading')
            }
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(phase.progress.progress * 100)}
          >
            <i style={{ width: `${Math.round(phase.progress.progress * 100)}%` }} />
          </span>
          <p className="text-xs text-ink-muted">
            {t('letter.progress', { percent: Math.round(phase.progress.progress * 100) })}
          </p>
          <button
            type="button"
            onClick={() => {
              abort.current?.abort();
              setPhase({ name: 'start', typing: false });
            }}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('letter.cancel')}
          </button>
        </div>
      ) : null}

      {phase.name === 'review' && reading ? (
        <>
          <details
            className="kw-tile"
            open={textOpen}
            onToggle={(event) => setTextOpen((event.target as HTMLDetailsElement).open)}
          >
            <summary className="min-h-11 cursor-pointer py-2 font-semibold">
              {t('letter.recognized')}
            </summary>
            <div className="mt-2 flex flex-col gap-2">
              {text.trim() === '' ? (
                <p className="text-sm text-warn">{t('letter.emptyText')}</p>
              ) : null}
              <p className="text-xs text-ink-muted">{t('letter.recognizedHint')}</p>
              <TextInput
                id={ids.text}
                value={text}
                onChange={setText}
                onAnalyze={() => analyze(text)}
                again
              />
            </div>
          </details>

          {reading.skipped.length > 0 ? (
            <section aria-labelledby="letter-skipped" className="kw-tile flex flex-col gap-1">
              <h3 id="letter-skipped" className="kw-kicker">
                {t('letter.skipped')}
              </h3>
              <ul className="flex flex-col gap-1 text-sm">
                {reading.skipped.map((entry) => (
                  <li key={`${entry.reason}-${entry.date}-${entry.sourceLine}`}>
                    <span className="font-medium">
                      {t(`letter.skip.${entry.reason}`)} ·{' '}
                      {formatDay(entry.date, 'date', i18n.language)}
                    </span>
                    <span className="block text-xs text-ink-muted">
                      {t('letter.source', { line: entry.sourceLine })}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {rows.length === 0 ? (
            <div className="kw-tile flex flex-col gap-2">
              <p className="text-sm">{t('letter.nothing')}</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setTextOpen(true)}
                  className="kw-btn kw-btn-quiet kw-btn-small flex-1"
                >
                  {t('letter.nothingWay')}
                </button>
                <button
                  type="button"
                  onClick={back}
                  className="kw-btn kw-btn-quiet kw-btn-small flex-1"
                >
                  {t('letter.newEvent')}
                </button>
              </div>
            </div>
          ) : (
            <>
              {householdId && people.length > 1 ? (
                <label htmlFor={ids.forWhom} className="kw-tile flex flex-col gap-1">
                  <span className="kw-label">{t('letter.forWhom')}</span>
                  <select
                    id={ids.forWhom}
                    value={letterFor}
                    onChange={(event) => {
                      setForWhom(event.target.value);
                      setRows((current) => current.map((row) => ({ ...row, forUserId: null })));
                    }}
                    className="kw-field"
                  >
                    <option value="">{t('letter.forAll')}</option>
                    {people.map((member) => (
                      <option key={member.userId} value={member.userId}>
                        {member.displayName}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <section aria-labelledby="letter-suggestions" className="flex flex-col gap-2">
                <h3 id="letter-suggestions" className="kw-kicker">
                  {t('letter.suggestions')}
                </h3>
                <p className="text-xs text-ink-muted">{t('letter.suggestionsHint')}</p>
                <ul className="flex flex-col gap-2">
                  {rows.map((row, index) => (
                    <SuggestionRow
                      key={row.key}
                      row={resolve(row)}
                      suggestion={reading.suggestions[index]!}
                      householdId={householdId ?? null}
                      people={people}
                      lists={usableLists}
                      onChange={(change) => update(row.key, change)}
                    />
                  ))}
                </ul>
              </section>

              {incomplete ? <p className="text-sm text-warn">{t('letter.incomplete')}</p> : null}
              {problem === 'save' ? (
                <p role="alert" className="text-sm text-danger">
                  {t('letter.error.save')}
                </p>
              ) : null}
              <button
                type="button"
                onClick={save}
                disabled={chosen.length === 0 || incomplete || saving}
                className="kw-btn"
              >
                {saving ? t('letter.applying') : t('letter.apply', { count: chosen.length })}
              </button>
            </>
          )}
        </>
      ) : null}

      {phase.name === 'saved' ? (
        <div className="kw-tile flex flex-col gap-3" role="status">
          <p className="font-semibold text-good">
            {t('letter.saved', { events: phase.events, tasks: phase.tasks })}
          </p>
          <p className="text-sm text-ink-muted">{t('letter.savedHint')}</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={onClose} className="kw-btn flex-1">
              {t('letter.done')}
            </button>
            <button type="button" onClick={restart} className="kw-btn kw-btn-quiet flex-1">
              {t('letter.another')}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function TextInput({
  id,
  value,
  onChange,
  onAnalyze,
  again = false,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onAnalyze: () => void;
  again?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="flex flex-col gap-1">
        <span className="kw-label">{t('letter.textLabel')}</span>
        <textarea
          id={id}
          value={value}
          rows={8}
          maxLength={20_000}
          onChange={(event) => onChange(event.target.value)}
          className="kw-field font-mono text-sm"
        />
      </label>
      {again ? null : <p className="text-xs text-ink-muted">{t('letter.textHint')}</p>}
      <button
        type="button"
        onClick={onAnalyze}
        disabled={value.trim() === ''}
        className="kw-btn kw-btn-small"
      >
        {again ? t('letter.reanalyze') : t('letter.analyze')}
      </button>
    </div>
  );
}

function SuggestionRow({
  row,
  suggestion,
  householdId,
  people,
  lists,
  onChange,
}: {
  row: LetterRow;
  suggestion: LetterSuggestion;
  householdId: string | null;
  people: LocalHouseholdMember[];
  lists: LocalTaskList[];
  onChange: (change: Partial<LetterRow>) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const ids = {
    heading: useId(),
    title: useId(),
    date: useId(),
    time: useId(),
    end: useId(),
    list: useId(),
    for: useId(),
  };
  const tone =
    suggestion.confidence === 'high'
      ? 'text-good'
      : suggestion.confidence === 'medium'
        ? 'text-ink-muted'
        : 'text-warn';
  return (
    <li
      aria-labelledby={ids.heading}
      className={`kw-tile flex flex-col gap-3 ${row.selected ? '' : 'opacity-80'}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 id={ids.heading} className="min-w-0 flex-1 font-semibold break-words">
          {row.title || t('letter.noTitle')}
        </h4>
        <button
          type="button"
          aria-pressed={row.selected}
          onClick={() => onChange({ selected: !row.selected })}
          className="kw-toggle shrink-0"
        >
          {t('letter.take')}
        </button>
      </div>
      <p className={`text-xs font-semibold ${tone}`}>
        {t(`letter.confidence.${suggestion.confidence}`)}
        {suggestion.doubts.length > 0
          ? ` · ${suggestion.doubts.map((doubt) => t(`letter.doubt.${doubt}`)).join(' · ')}`
          : ''}
      </p>
      <p className="text-xs break-words text-ink-muted">
        {t('letter.source', { line: suggestion.sourceLine })}
      </p>

      {row.selected ? (
        <>
          <div role="group" aria-label={t('letter.kindLabel')} className="flex gap-2">
            {(['event', 'task'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                aria-pressed={row.kind === kind}
                onClick={() => onChange({ kind })}
                className="kw-toggle flex-1"
              >
                {t(`letter.kind.${kind}`)}
              </button>
            ))}
          </div>
          <label htmlFor={ids.title} className="flex flex-col gap-1">
            <span className="kw-label">{t('letter.title')}</span>
            <input
              id={ids.title}
              value={row.title}
              maxLength={200}
              onChange={(event) => onChange({ title: event.target.value })}
              className="kw-field"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <label htmlFor={ids.date} className="flex w-40 flex-col gap-1">
              <span className="kw-label">
                {row.kind === 'event' ? t('letter.date') : t('letter.dueDate')}
              </span>
              <input
                id={ids.date}
                type="date"
                value={row.date}
                onChange={(event) => onChange({ date: event.target.value })}
                className="kw-field"
              />
            </label>
            {row.endDate && row.kind === 'event' ? (
              <label htmlFor={ids.end} className="flex w-40 flex-col gap-1">
                <span className="kw-label">{t('letter.endDate')}</span>
                <input
                  id={ids.end}
                  type="date"
                  value={row.endDate}
                  min={row.date}
                  onChange={(event) => onChange({ endDate: event.target.value || null })}
                  className="kw-field"
                />
              </label>
            ) : (
              <label htmlFor={ids.time} className="flex w-28 flex-col gap-1">
                <span className="kw-label">{t('letter.time')}</span>
                <input
                  id={ids.time}
                  type="time"
                  value={row.time}
                  onChange={(event) => onChange({ time: event.target.value })}
                  className="kw-field"
                />
              </label>
            )}
          </div>
          {row.date === '' ? <p className="text-xs text-warn">{t('letter.noDate')}</p> : null}
          {row.title.trim() === '' ? (
            <p className="text-xs text-warn">{t('letter.noTitle')}</p>
          ) : null}

          {row.kind === 'task' ? (
            <label htmlFor={ids.list} className="flex flex-col gap-1">
              <span className="kw-label">{t('letter.list')}</span>
              <select
                id={ids.list}
                value={row.listId}
                onChange={(event) => onChange({ listId: event.target.value })}
                className="kw-field"
              >
                {lists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.name}
                  </option>
                ))}
                <option value={NEW_LIST}>
                  {t('letter.newList', { name: t('letter.newListName') })}
                </option>
              </select>
            </label>
          ) : null}

          {householdId && people.length > 1 ? (
            <label htmlFor={ids.for} className="flex flex-col gap-1">
              <span className="kw-label">{t('letter.for')}</span>
              <select
                id={ids.for}
                value={row.forUserId}
                onChange={(event) => onChange({ forUserId: event.target.value })}
                className="kw-field"
              >
                <option value="">{t('letter.forAll')}</option>
                {people.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.displayName}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </>
      ) : null}
    </li>
  );
}
